/*
 * NattyPLUS Tab Product Slider — shared controller
 * Used by sections/np-tab-product-slider*.liquid. Loaded ONCE via theme.liquid.
 * Initializes every [data-np-tps] root on the page; the per-section CSS lives
 * inline in each section (scoped to .np-tps-section--N).
 *
 * 2026-10 — quick-buy modal gallery brought in line with the PDP:
 *   - VARIANT SWITCHING NOW WORKS. The old matcher tested the option token as a
 *     plain substring of the alt, but the option is "No Melatonin"
 *     ("nomelatonin") while the alt reads "30 day no mel" ("30daynomel") — it
 *     never matched, so changing the variant left the image alone. pickModalSlide
 *     is now the PDP's _matchAndCrossfade scoring verbatim (matches the alt
 *     remainder against the option both ways and by prefix).
 *   - Per-plan image isolation, like the PDP: picking 90-day hides the 30-day
 *     slides and their dots, and vice versa; untagged/shared images stay for
 *     every plan (applyModalPlanFilter). Needs the [hidden] rules at the end of
 *     np-tps.css, because .np-tps-modal__slide sets display:flex.
 *   - The swap is INSTANT (behavior:'auto'), not a smooth scroll across every
 *     slide in between.
 *   Because slides can be hidden, every index in wireGallery is an index into
 *   the VISIBLE slides; dots keep their original order and hide with their slide.
 *   To disable the isolation, make applyModalPlanFilter return right after its
 *   un-hide loop.
 */
(function () {
  'use strict';

  /* ── Shared PDP prefetch cache ─────────────────────────────────
     The quick-buy modal needs each product's rendered subscription widget,
     which lives only on the PDP. We fetch the PDP HTML and cache the PROMISE
     by URL, so: (1) hovering/pressing a card's Add-To-Cart can warm the cache
     before the click, making the popup options appear instantly, and (2) the
     same product opened twice never re-fetches. Shared across all sliders. */
  var pdpCache = {};
  function getPdpHtml(url) {
    var clean = url.split('#')[0];
    if (!pdpCache[clean]) {
      pdpCache[clean] = fetch(clean, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .catch(function (e) {
          delete pdpCache[clean]; // allow a retry on next open
          throw e;
        });
    }
    return pdpCache[clean];
  }

  /* ── Controller (one per [data-np-tps] root) ─────────────────── */
  function initRoot(root) {
    if (root.__npTpsBound) return;
    root.__npTpsBound = true;

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-np-tps-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-np-tps-panel]'));

    function selectTab(idx) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-np-tps-tab') === String(idx);
        t.classList.toggle('np-tps__tab--active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-np-tps-panel') === String(idx);
        if (on) {
          p.removeAttribute('hidden');
        } else {
          p.setAttribute('hidden', '');
        }
      });
      var active = root.querySelector('[data-np-tps-panel="' + idx + '"]');
      if (active) updateArrows(active);
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        selectTab(t.getAttribute('data-np-tps-tab'));
      });
    });

    /* Prefetch the PDP (for the quick-buy widget) on intent — hovering or
       pressing a card's Add-To-Cart warms getPdpHtml's cache so the popup's
       purchase options are ready the instant the modal opens. pointerdown
       fires ~100-200ms before click, so the fetch overlaps the press. */
    function warm(e) {
      var cta = e.target.closest('[data-np-tps-cta]');
      if (!cta || !root.contains(cta)) return;
      var url = cta.getAttribute('data-product-url');
      if (url) { try { getPdpHtml(url); } catch (err) {} }
    }
    root.addEventListener('pointerenter', warm, true);
    root.addEventListener('pointerdown', warm, true);

    /* Click routing:
         • Add-To-Cart button  → open the quick-buy MODAL for that product.
         • Card body            → open the product page in a NEW tab.
       Navigation/opening is done in JS so dragJustEnded can suppress the
       synthetic click a drag fires (otherwise a swipe would trigger one).
       We always preventDefault on the card link so its native href never
       double-fires. */
    var dragJustEnded = false;
    root.addEventListener(
      'click',
      function (e) {
        var card = e.target.closest('[data-np-tps-card]');
        if (card && root.contains(card)) e.preventDefault();

        if (dragJustEnded) {
          dragJustEnded = false;
          e.stopPropagation();
          return;
        }

        var cta = e.target.closest('[data-np-tps-cta]');
        if (cta && root.contains(cta)) {
          e.stopPropagation();
          openModal(cta.getAttribute('data-product-url'), cta.getAttribute('data-product-id'));
          return;
        }

        if (card && root.contains(card)) {
          e.stopPropagation();
          var href = card.getAttribute('href');
          if (href) window.open(href, '_blank', 'noopener');
        }
      },
      true // capture: beat the native link navigation + stop drag-clicks early
    );

    /* ── Quick-buy modal ───────────────────────────────────────────
       Fetch the product page, lift its rendered np-subscription-widget +
       a small header into the modal, and run any inline script the
       fetched fragment carries so the widget initialises (plan picker,
       variant picker, add-to-cart → NPCartSync). */
    // ONE shared modal for the whole page (rendered once in theme.liquid,
    // outside any section). Every slider root opens this same element; only its
    // event wiring is deduped (see modal.__npTpsBound below). Each root's own
    // openModal() closes over its `root`, so readData() resolves the right card.
    var modal = document.querySelector('[data-np-tps-modal]');
    var modalBody = modal ? modal.querySelector('[data-np-tps-modal-body]') : null;
    var loadingHTML = modalBody ? modalBody.innerHTML : '';
    var lastFocus = null;

    function lockScroll(on) {
      document.documentElement.classList.toggle('np-tps-modal__open', on);
      document.body.style.overflow = on ? 'hidden' : '';
    }

    function showModal() {
      if (!modal) return;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      // next frame → trigger the open transition
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
      });
    }

    function closeModal() {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.classList.remove('np-tps-modal--ready'); // re-armed on the next open
      // Always release OUR scroll lock. The cart drawer manages its own lock
      // independently, so if it's open it keeps the page locked regardless;
      // leaving our lock on would strand the page unscrollable after both close.
      lockScroll(false);
      var done = function () {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        if (modalBody) modalBody.innerHTML = loadingHTML; // reset to spinner
        modal.removeEventListener('transitionend', done);
      };
      modal.addEventListener('transitionend', done);
      // Fallback in case transitionend doesn't fire.
      setTimeout(done, 350);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    // Re-create script tags found in injected HTML (innerHTML won't run them).
    function runScripts(container) {
      var scripts = container.querySelectorAll('script');
      scripts.forEach(function (old) {
        var s = document.createElement('script');
        for (var i = 0; i < old.attributes.length; i++) {
          s.setAttribute(old.attributes[i].name, old.attributes[i].value);
        }
        s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
      });
    }

    // Copy the subscription-widget's inline CSS from the fetched PDP into
    // <head> (once) so the imported widget is styled exactly like the PDP.
    // The widget's styles live in a <style> block inside its snippet, keyed
    // by the .np-sub-widget / .np-card class names.
    // Copy the widget's CSS from the fetched PDP so the modal matches the PDP.
    // This includes the PER-PRODUCT gold gradient block from theme.liquid (it
    // targets .np-card--gold / .np-cta, keyed by request.path). Because that
    // gradient differs per product, we RE-INJECT on every open into a single
    // replaceable <style id="np-tps-widget-css"> — otherwise every product would
    // show the FIRST-opened product's gradient. Safe to keep global: the only
    // .np-card/.np-cta elements on the host page live inside the modal.
    function ensureWidgetCss(doc) {
      var css = '';
      doc.querySelectorAll('style').forEach(function (st) {
        var t = st.textContent || '';
        if (t.indexOf('.np-sub-widget') > -1 || t.indexOf('.np-card') > -1 || t.indexOf('.np-cta') > -1) {
          css += t + '\n';
        }
      });
      if (!css) return;
      var style = document.getElementById('np-tps-widget-css');
      if (!style) {
        style = document.createElement('style');
        style.id = 'np-tps-widget-css';
        document.head.appendChild(style);
      }
      style.textContent = css; // replace so the active product's gradient wins
    }

    // Bring two PDP details into the imported widget (the PDP's Idea6 script
    // does both there, but it only runs on product templates):
    //  1. The 90-day card's ticks are a different (white) icon from the
    //     30-day's green ones — copy the 30-day tick over so they match.
    //  2. --np-bb-accent drives the selected One-Time card's radio fill and
    //     ring. On the PDP it comes from .product[data-np-palette], which the
    //     modal doesn't have, so it fell back to white. Take it from the
    //     product gradient the 30-day card is already painted with (the
    //     middle stop is the per-product accent in layout/theme.liquid's map).
    function matchPdpDetails(widget) {
      var goldCheck = widget.querySelector('.np-card--gold .np-benefits .np-i-check');
      if (goldCheck) {
        widget.querySelectorAll('.np-card--dark .np-benefits .np-i-check').forEach(function (svg) {
          svg.parentNode.replaceChild(goldCheck.cloneNode(true), svg);
        });
      }
      // Read the gradient + ink from the injected per-product CSS rather than
      // from the 30-day card's computed style: unselected cards are repainted
      // dark in the modal (np-tps.css), so the card only shows the gradient
      // while it happens to be the selected one.
      var css = (document.getElementById('np-tps-widget-css') || {}).textContent || '';
      // `.np-active` optional: theme.liquid's rule became
      // `.np-card--gold.np-active, .np-card--dark.np-active, …` (2026-10, the
      // gradient now marks the selected plan only), which the old pattern —
      // `.np-card--gold, .np-card--dark,` — no longer matched, so
      // --np-tps-prod-grad stayed empty and the selected variant chip fell
      // back to a near-invisible amber tint.
      var m = css.match(/\.np-card--gold(?:\.np-active)?,\s*\.np-card--dark(?:\.np-active)?,[^{]*\{\s*background:\s*(linear-gradient\([^;]*?\))\s*!important;\s*color:\s*([^;!]+?)\s*!important/);
      if (m) {
        widget.style.setProperty('--np-tps-prod-grad', m[1]);
        widget.style.setProperty('--np-tps-prod-ink', m[2]);
        var stops = m[1].match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/gi);
        if (stops && stops.length >= 2) widget.style.setProperty('--np-bb-accent', stops[1]);
      }
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    var STAR =
      '\x3csvg viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '\x3cpath d="M17.918 6.584Q17.918 6.201 17.326 6.096L11.926 5.295L9.523 0.418Q9.314 0 9 0Q8.686 0 8.477 0.418L6.074 5.295L0.674 6.096Q0.082 6.201 0.082 6.584Q0.082 6.828 0.361 7.107L4.262 10.904L3.322 16.268Q3.322 16.408 3.322 16.477Q3.322 16.686 3.426 16.843Q3.531 17 3.775 17Q3.949 17 4.193 16.861L9 14.352L13.807 16.861Q14.051 17 14.225 17Q14.469 17 14.574 16.843Q14.678 16.686 14.678 16.477Q14.678 16.338 14.678 16.268L13.738 10.904L17.639 7.107Q17.918 6.828 17.918 6.584Z" fill="url(#np-tps-gold)"/>\x3c/svg>';
    var CHEV =
      '\x3csvg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '\x3cpath d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\x3c/svg>';

    // Read the per-card data blob rendered server-side next to the CTA.
    // Searches the whole DOCUMENT (not just this slider root) so the SAME modal
    // works for callers outside any slider — e.g. the Shop All grid, which renders
    // its own [data-np-tps-data] blob and calls window.NPTps.openModal. Product ids
    // are unique per page, so a document-wide lookup resolves the right card.
    function readData(productId) {
      if (!productId) return null;
      var node = document.querySelector('[data-np-tps-data="' + productId + '"]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    // Build the two-column shell. Returns the [data-np-tps-widget-slot] node
    // the fetched widget gets injected into.
    // Branded metal icon behind the leading shot (and the 90-day shot), as on
    // the PDP. Same handle → logo map as the PDP block in layout/theme.liquid
    // ("Branded icon behind the LEADING product image") — keep the two in sync.
    // Bundles have their own artwork and aren't listed, so they get no icon.
    var LEAD_LOGOS = {
      'ultimate-testosterone-booster': 'logo_test_metal.svg?v=1782293690',
      'ultimate-multivitamin-for-men': 'logo_multi_metal.svg?v=1782293690',
      'ultimate-sleep-support': 'logo_sleepm_metal.svg?v=1782293690',
      'ultimate-travel-support': 'logo_travel_metal.svg?v=1782293690',
      'ultimate-cholesterol-support': 'logo_chol_metal.svg?v=1782293690',
      'ultimate-estrogen-reduction': 'logo_est_metal.svg?v=1782293690',
      'ultimate-liver-support': 'logo_liver_metal.svg?v=1782293690',
      'ultimate-blood-sugar-management': 'logo_bs_metal.svg?v=1782293690',
      'ultimate-ashwagandha-complex': 'logo_ash_metal.svg?v=1782293690'
    };
    function leadLogoFor(url) {
      var m = String(url || '').match(/\/products\/([^/?#]+)/);
      var file = m && LEAD_LOGOS[m[1]];
      return file ? 'https://cdn.shopify.com/s/files/1/0938/1897/8590/files/' + file : '';
    }

    function buildLayout(data, clean) {
      var imgs = (data && data.images) || [];
      var leadLogo = leadLogoFor(clean);
      var slides = imgs
        .map(function (im, i) {
          // Images may be {src, alt} (new) or a plain url string (legacy).
          var src = im && im.src != null ? im.src : im;
          var alt = im && im.alt ? im.alt : '';
          // Every plan shot gets the icon — alt "30 day …" / "90 day …", any
          // variant ("30 day no mel", "90 day mel") — so it stays when the
          // shopper switches plan or variant (No Melatonin used to land on a
          // bare shot). Lifestyle/ingredient images carry no plan token.
          var withLogo = leadLogo && (i === 0 || /^\d+day/.test(normAlt(alt)));
          return (
            '\x3cdiv class="np-tps-modal__slide' + (withLogo ? ' np-tps-modal__slide--logo' : '') + '"' +
            (withLogo ? ' style="--np-lead-logo:url(\'' + esc(leadLogo) + '\')"' : '') +
            ' data-np-tps-slide-alt="' + esc(alt) + '">' +
            '\x3cimg src="' + esc(src) + '" alt="' + esc(alt) + '" loading="lazy">\x3c/div>'
          );
        })
        .join('');
      var dots = imgs
        .map(function (_, i) {
          return (
            '\x3cbutton type="button" class="np-tps-modal__dot' +
            (i === 0 ? ' is-active' : '') +
            '" data-np-tps-modal-dot="' + i + '" aria-label="Go to image ' + (i + 1) + '">\x3c/button>'
          );
        })
        .join('');

      var media =
        '\x3cdiv class="np-tps-modal__media">' +
        '\x3cdiv class="np-tps-modal__gallery" data-np-tps-modal-gallery>' + slides + '\x3c/div>' +
        (imgs.length > 1
          ? '\x3cbutton type="button" class="np-tps-modal__arrow np-tps-modal__arrow--prev" data-np-tps-modal-garrow="prev" aria-label="Previous image">' +
            '\x3csvg viewBox="0 0 24 24" fill="none" aria-hidden="true">\x3cpath d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\x3c/svg>\x3c/button>' +
            '\x3cbutton type="button" class="np-tps-modal__arrow np-tps-modal__arrow--next" data-np-tps-modal-garrow="next" aria-label="Next image">' +
            '\x3csvg viewBox="0 0 24 24" fill="none" aria-hidden="true">\x3cpath d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\x3c/svg>\x3c/button>' +
            '\x3cdiv class="np-tps-modal__dots">' + dots + '\x3c/div>'
          : '') +
        '\x3c/div>';

      var ratingHTML = '';
      if (data && data.ratingCount > 0) {
        ratingHTML =
          '\x3cdiv class="np-tps-modal__rating">' +
          '\x3cspan class="np-tps-modal__stars">' + STAR + STAR + STAR + STAR + STAR + '\x3c/span>' +
          '\x3cspan>' + (Math.round(data.rating * 10) / 10) + '/5 · ' + data.ratingCount + ' reviews\x3c/span>' +
          '\x3c/div>';
      }

      var descHTML = '';
      if (data && data.description) {
        descHTML =
          '\x3cdiv class="np-tps-modal__desc" data-np-tps-modal-desc>' +
          '\x3cbutton type="button" class="np-tps-modal__desc-toggle" data-np-tps-modal-desc-toggle>' +
          '\x3cspan>View More Information\x3c/span>' + CHEV +
          '\x3c/button>' +
          '\x3cdiv class="np-tps-modal__desc-body">' + data.description + '\x3c/div>' +
          '\x3c/div>';
      }

      // Title links to the PDP (new tab). Falls back to plain text if no URL.
      var titleText = esc(data ? data.title : '');
      var titleInner =
        data && data.url
          ? '\x3ca class="np-tps-modal__title-link" href="' + esc(data.url) + '" target="_blank" rel="noopener">' + titleText + '\x3c/a>'
          : titleText;

      var info =
        '\x3cdiv class="np-tps-modal__info">' +
        (data && data.subtitle ? '\x3cp class="np-tps-modal__subtitle">' + esc(data.subtitle) + '\x3c/p>' : '') +
        '\x3ch2 class="np-tps-modal__title">' + titleInner + '\x3c/h2>' +
        ratingHTML +
        descHTML +
        '\x3cdiv class="np-tps-modal__widget" data-np-tps-widget-slot>' +
        '\x3cdiv class="np-tps-modal__loading">\x3cspan class="np-tps-modal__spinner" aria-hidden="true">\x3c/span>\x3cspan>Loading options…\x3c/span>\x3c/div>' +
        '\x3c/div>' +
        '\x3c/div>';

      modalBody.innerHTML = '\x3cdiv class="np-tps-modal__grid">' + media + info + '\x3c/div>';
      wireGallery();
      wireDesc();
      return modalBody.querySelector('[data-np-tps-widget-slot]');
    }

    // Image carousel inside the modal (snap scroll + dots + arrows).
    //
    // Per-plan image isolation (mirrors the PDP's applyGalleryPlanFilter) means
    // slides can be HIDDEN, so every index here is an index into the VISIBLE
    // slides, never into the full list. The dots keep their original order and
    // hide alongside their slide. Unlike the PDP we can simply use `hidden`:
    // this is a scroll-snap strip, not Embla, so there is no snap math to
    // confuse — that is why the PDP has to physically detach nodes and this
    // does not.
    var galleryApi = null;

    function wireGallery() {
      galleryApi = null;
      var gallery = modalBody.querySelector('[data-np-tps-modal-gallery]');
      if (!gallery) return;
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('.np-tps-modal__slide'));
      var dots = Array.prototype.slice.call(modalBody.querySelectorAll('[data-np-tps-modal-dot]'));
      var prev = modalBody.querySelector('[data-np-tps-modal-garrow="prev"]');
      var next = modalBody.querySelector('[data-np-tps-modal-garrow="next"]');

      function step() {
        // clientWidth can be 0 for a frame right after the modal opens.
        return gallery.clientWidth || (slides[0] && slides[0].offsetWidth) || 0;
      }
      function visibleSlides() {
        return slides.filter(function (sl) {
          return !sl.hidden;
        });
      }
      function activeIndex() {
        var w = step();
        return w ? Math.round(gallery.scrollLeft / w) : 0;
      }
      function goTo(i, instant) {
        var w = step();
        if (!w) return;
        gallery.scrollTo({ left: w * i, behavior: instant ? 'auto' : 'smooth' });
      }
      function syncDots() {
        var current = visibleSlides()[activeIndex()];
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', slides[i] === current);
        });
      }

      gallery.addEventListener('scroll', syncDots, { passive: true });
      dots.forEach(function (d, i) {
        d.addEventListener('click', function () {
          var vi = visibleSlides().indexOf(slides[i]);
          if (vi > -1) goTo(vi);
        });
      });
      if (prev)
        prev.addEventListener('click', function () {
          gallery.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      if (next)
        next.addEventListener('click', function () {
          gallery.scrollBy({ left: step(), behavior: 'smooth' });
        });

      galleryApi = {
        gallery: gallery,
        slides: slides,
        dots: dots,
        visibleSlides: visibleSlides,
        goTo: goTo,
        syncDots: syncDots,
      };
    }

    // ── Plan/variant-driven image switching (mirrors the PDP) ────────────
    // The imported widget dispatches `np:selection-change` when the shopper
    // picks a plan card or variant. We pick the slide whose ALT text matches the
    // plan token (30 day / 90 day) + the chosen variant option and scroll the
    // popup gallery to it — the same alt-matching the PDP gallery uses.
    // Registered ONCE (not per modal open) so listeners don't accumulate; it
    // resolves the live gallery at event time.
    function normAlt(s) {
      return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    function variantOptionTokens(variantId) {
      var tokens = [];
      try {
        var dataEl = modalBody.querySelector('[data-np-variants]');
        if (!dataEl) return tokens;
        var vs = JSON.parse(dataEl.textContent);
        var wanted = Number(variantId);
        var v = vs.find(function (x) { return Number(x.id) === wanted; });
        if (!v) return tokens;
        ['option1', 'option2', 'option3'].forEach(function (k) {
          var t = normAlt(v[k]);
          if (t && ['default', 'defaulttitle', 'standard', 'all'].indexOf(t) === -1) tokens.push(t);
        });
      } catch (e) {}
      return tokens;
    }
    // The plan tokens this product's gallery knows about, read from the widget's
    // own cards (same source the PDP uses), falling back to the two we ship.
    function modalPlanTokens() {
      var toks = [];
      Array.prototype.forEach.call(modalBody.querySelectorAll('.np-card[data-image-alt]'), function (c) {
        var t = normAlt(c.getAttribute('data-image-alt'));
        if (t && toks.indexOf(t) === -1) toks.push(t);
      });
      return toks.length ? toks : ['30day', '90day'];
    }

    // Hide every slide (and its dot) belonging to a DIFFERENT plan, exactly as
    // the PDP gallery does: a slide is hidden only if its alt carries another
    // plan's token AND not the active one. Shared/untagged images (ingredients,
    // lifestyle shots) have no token, so they stay visible for every plan.
    // Fully reversible — we un-hide everything each run before re-hiding.
    function applyModalPlanFilter(api, keepToken) {
      var hideTokens = modalPlanTokens().filter(function (t) {
        return t && t !== keepToken;
      });
      api.slides.forEach(function (sl, i) {
        sl.hidden = false;
        if (api.dots[i]) api.dots[i].hidden = false;
      });
      if (!keepToken || !hideTokens.length) return;

      api.slides.forEach(function (sl, i) {
        var alt = normAlt(sl.getAttribute('data-np-tps-slide-alt'));
        if (!alt || alt.indexOf(keepToken) > -1) return;
        var other = hideTokens.some(function (t) {
          return alt.indexOf(t) > -1;
        });
        if (!other) return;
        sl.hidden = true;
        if (api.dots[i]) api.dots[i].hidden = true;
      });

      // Never leave an empty gallery (e.g. a product with no 90-day images at
      // all) — show everything again rather than nothing.
      if (!api.visibleSlides().length) {
        api.slides.forEach(function (sl, i) {
          sl.hidden = false;
          if (api.dots[i]) api.dots[i].hidden = false;
        });
      }
    }

    // Pick the best slide for (plan token + variant options).
    //
    // This is the PDP's _matchAndCrossfade scoring, ported verbatim, and it is
    // the fix for "the variant switch doesn't change the image here". The old
    // code tested `alt.indexOf(optionToken)`, a plain substring match — but the
    // option is "No Melatonin" ("nomelatonin") while the alt reads "30 day no
    // mel" ("30daynomel"), so it never matched and the variant scored nothing.
    // Matching the remainder BOTH ways (and by prefix) is what connects the
    // abbreviated alt to the full option name.
    function pickModalSlide(slides, token, optTokens) {
      var hasVariant = optTokens.length > 0;
      var best = -1;
      var bestScore = -1;
      slides.forEach(function (slide, i) {
        var alt = normAlt(slide.getAttribute('data-np-tps-slide-alt'));
        if (!alt || !token || alt.indexOf(token) === -1) return;

        var remainder = alt.replace(token, '');
        if (remainder.charAt(0) === 's') remainder = remainder.substring(1);

        var score = 1;
        if (hasVariant && remainder.length > 0) {
          var allMatch = true;
          var matchLen = 0;
          for (var k = 0; k < optTokens.length; k++) {
            var ot = optTokens[k];
            if (remainder.indexOf(ot) > -1 || ot.indexOf(remainder) > -1) {
              matchLen += Math.min(ot.length, remainder.length);
            } else {
              var found = false;
              for (var pLen = ot.length; pLen >= 3; pLen--) {
                if (remainder.indexOf(ot.substring(0, pLen)) > -1) {
                  matchLen += pLen;
                  found = true;
                  break;
                }
              }
              if (!found) {
                allMatch = false;
                break;
              }
            }
          }
          if (allMatch) {
            var leftover = remainder.length - matchLen;
            if (leftover < 0) leftover = 0;
            score = 100 + matchLen * 10 - leftover;
          } else {
            score = 0;
          }
        } else if (hasVariant) {
          score = 1;
        } else {
          score = 10 - remainder.length;
        }

        if (score > bestScore) {
          bestScore = score;
          best = i;
        }
      });
      return best;
    }

    function syncModalGalleryToSelection(detail) {
      if (!modal || modal.hidden) return; // only when the popup is open
      var api = galleryApi;
      if (!api || !api.gallery.isConnected || api.slides.length < 2) return;

      var d = detail || {};
      var keepToken = normAlt(d.months === 3 ? '90 day' : '30 day');

      // Isolate this plan's images first, then match within what is left — so
      // we can only ever land on a slide the shopper can actually see.
      applyModalPlanFilter(api, keepToken);

      var visible = api.visibleSlides();
      var best = pickModalSlide(visible, keepToken, variantOptionTokens(d.variantId));
      // Instant, like the PDP: no animated travel across the slides in between.
      api.goTo(best > -1 ? best : 0, true);
      api.syncDots();
    }

    // Description dropdown toggle.
    function wireDesc() {
      var wrap = modalBody.querySelector('[data-np-tps-modal-desc]');
      if (!wrap) return;
      var toggle = wrap.querySelector('[data-np-tps-modal-desc-toggle]');
      toggle.addEventListener('click', function () {
        wrap.classList.toggle('is-open');
      });
    }

    var fetchSeq = 0;
    function openModal(url, productId) {
      if (!modal || !modalBody || !url) {
        // No modal available — fall back to opening the product page.
        if (url) window.open(url, '_blank', 'noopener');
        return;
      }
      lastFocus = document.activeElement;
      var clean = url.split('#')[0];

      // Build the shell immediately from server-rendered data (no wait).
      var data = readData(productId);
      var slot = buildLayout(data, clean);
      // The selection-change listener is registered ONCE, by whichever slider
      // initialised first — but galleryApi lives in THIS root's closure. Point
      // the shared modal at this root's sync so plan/variant changes drive the
      // gallery the popup is actually showing (sections 2 and 3 on the
      // homepage used to read section 1's stale, disconnected gallery).
      modal.__npTpsSync = syncModalGalleryToSelection;
      showModal();

      // Fetch the rendered subscription widget from the PDP (cached/prefetched).
      var seq = ++fetchSeq;
      getPdpHtml(clean)
        .then(function (html) {
          if (seq !== fetchSeq || !slot) return; // superseded
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var widget = doc.querySelector('[data-np-widget]');
          if (!widget) {
            slot.innerHTML =
              '\x3cdiv class="np-tps-modal__error">Couldn\'t load options. ' +
              '\x3ca href="' + clean + '" style="color:#f59e0b">View product →\x3c/a>\x3c/div>';
            return;
          }
          // Lift the widget's CSS (incl. the per-product gold gradient) from the
          // fetched PDP so the modal matches that product's theme. Re-injected
          // each open so switching products updates the gradient.
          ensureWidgetCss(doc);

          slot.innerHTML = '';
          slot.appendChild(widget);

          /* Arm the Add-To-Cart hover sweep only AFTER first paint, the same
             way initRoot() arms .np-tps--ready on a slider. The sweep is
             one-directional (panel enters from the top, leaves out the bottom),
             which needs a separate OUT keyframe whose `from` state COVERS the
             button — so if it were live on the frame the widget mounts, it
             would play once unprompted and flash white across the button.
             closeModal() takes the class off again so every open re-arms. */
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              modal.classList.add('np-tps-modal--ready');
            });
          });
          matchPdpDetails(widget);

          // Boot the widget. If its class is already on the page just re-run
          // init via the hook it listens for; otherwise pull its own script
          // from the fetched page and execute it (its inline init() finds the
          // freshly-injected [data-np-widget] node).
          if (window.__npWidget || typeof window.NPWidget === 'function') {
            document.dispatchEvent(new CustomEvent('shopify:section:load'));
          } else {
            var pageScripts = doc.querySelectorAll('script');
            pageScripts.forEach(function (sc) {
              var txt = sc.textContent || '';
              if (txt.indexOf('data-np-widget') > -1 || txt.indexOf('NPWidget') > -1) {
                slot.appendChild(sc.cloneNode(true));
              }
            });
            runScripts(slot);
          }
        })
        .catch(function () {
          if (seq !== fetchSeq || !slot) return;
          slot.innerHTML =
            '\x3cdiv class="np-tps-modal__error">Couldn\'t load options. ' +
            '\x3ca href="' + clean + '" style="color:#f59e0b">View product →\x3c/a>\x3c/div>';
        });
    }

    // Wire the SHARED modal's global handlers ONCE (multiple roots reuse it).
    if (modal && !modal.__npTpsBound) {
      modal.__npTpsBound = true;
      modal.querySelectorAll('[data-np-tps-modal-close]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
      });

      // Close the modal as soon as the cart drawer opens (the widget's
      // add-to-cart calls NPCartSync.refresh({open:true}) → drawer opens).
      // We watch the cart drawer's open state rather than the add click, so
      // it closes exactly when the drawer actually appears.
      watchCartDrawer();

      // ALSO close on a completed add-to-cart, independent of the drawer. On the
      // CART PAGE, NPCartSync.refresh() targets <cart-page> (not the drawer), so the
      // drawer never opens and watchCartDrawer() never fires — the modal would stay
      // open after adding. Observing the /cart/add.js request settling closes the
      // modal everywhere (cart page, PDP, collection). We wrap fetch once, return the
      // ORIGINAL promise untouched (observation only), and only act on /cart/add.
      watchCartAdd();

      // Switch the popup's image when the imported widget reports a plan/variant
      // change (registered once on the shared modal — no per-open accumulation).
      document.addEventListener('np:selection-change', function (e) {
        var sync = modal.__npTpsSync || syncModalGalleryToSelection;
        sync(e && e.detail);
      });
    }

    // Expose the shared opener page-wide so callers OUTSIDE any slider (the Shop
    // All grid) can open the exact same quick-buy popup. openModal only needs the
    // shared modal element + a document-wide readData (both true above), so it works
    // regardless of which init created it. Set once; harmless to overwrite with an
    // equivalent function on re-init.
    window.NPTps = window.NPTps || {};
    window.NPTps.openModal = function (url, productId) {
      return openModal(url, productId);
    };

    function cartDrawerOpen(el) {
      if (!el) return false;
      return (
        el.isOpen === true ||
        el.classList.contains('is-opened') ||
        el.classList.contains('is-open') ||
        el.classList.contains('open') ||
        el.hasAttribute('open')
      );
    }

    function watchCartDrawer() {
      var drawer = document.querySelector('cart-drawer, #CartDrawer, [data-cart-drawer]');
      if (!drawer) {
        // Drawer not in the DOM yet — retry shortly (sections load async).
        setTimeout(watchCartDrawer, 600);
        return;
      }
      if (drawer.__npTpsWatched) return;
      drawer.__npTpsWatched = true;

      var obs = new MutationObserver(function () {
        if (modal && !modal.hidden && cartDrawerOpen(drawer)) {
          closeModal();
        }
      });
      obs.observe(drawer, {
        // 'is-opened' is how the redesigned <np-cart-drawer> signals open (an
        // attribute, not a class) — without it the observer never fires on the
        // new drawer and the modal is left open behind it. Old drawer used class.
        attributes: true,
        attributeFilter: ['is-opened', 'class', 'open', 'aria-hidden', 'style']
      });
    }

    // Close the modal when a /cart/add.js completes — the drawer-independent
    // fallback (see call site). Wrap fetch ONCE globally; observation only (returns
    // the original promise untouched). Small delay so any refresh/redraw that the
    // add triggers has started before the modal closes.
    function watchCartAdd() {
      if (window.__npTpsAddWatched || typeof window.fetch !== 'function') return;
      window.__npTpsAddWatched = true;
      var _fetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url);
        var p = _fetch.apply(this, arguments);
        if (url && /\/cart\/add(\.js|\b)/i.test(String(url))) {
          try {
            p.then(function () {
              setTimeout(function () {
                if (modal && !modal.hidden) closeModal();
              }, 150);
            }, function () {});
          } catch (e) {}
        }
        return p;
      };
    }

    /* Arrows: scroll the active panel's viewport by ~one card width. */
    function viewportOf(panel) {
      return panel.querySelector('[data-np-tps-viewport]');
    }
    /* Absolute scroll target for the next/previous card.

       This used to be a relative scrollBy(cardWidth + 18). The 18 was a
       hardcoded guess at the gap, but --np-tps-gap is 20px / 16px / 10px across
       the breakpoints, so every click was off by a few px — and since each
       scrollBy stacks on the last one, that error compounded until the cards had
       crept out from under the track's left gutter.

       Measuring each card's real offset and scrolling to it absolutely means the
       error can never accumulate: whatever the gap, the padding, or the card's
       min-width, every click lands a card exactly against the gutter. */
    function trackInset(vp) {
      var track = vp.querySelector('[data-np-tps-track]') || vp.firstElementChild;
      if (!track) return 0;
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    }
    function cardOffsets(vp) {
      var vpLeft = vp.getBoundingClientRect().left;
      return Array.prototype.map.call(
        vp.querySelectorAll('[data-np-tps-card]'),
        function (card) {
          // Offset within the scrollable content, independent of current scroll.
          return card.getBoundingClientRect().left - vpLeft + vp.scrollLeft;
        }
      );
    }
    function scrollByCard(vp, dir) {
      var offsets = cardOffsets(vp);
      var max = vp.scrollWidth - vp.clientWidth;
      if (!offsets.length) {
        vp.scrollBy({ left: dir * vp.clientWidth * 0.8, behavior: 'smooth' });
        return;
      }
      // Align the card to the gutter rather than to the viewport's hard edge,
      // so the first card's target is 0 and the rest keep the same inset.
      var inset = trackInset(vp);
      var cur = vp.scrollLeft;
      var target = dir > 0 ? max : 0;
      var i;
      if (dir > 0) {
        for (i = 0; i < offsets.length; i++) {
          if (offsets[i] - inset > cur + 2) {
            target = offsets[i] - inset;
            break;
          }
        }
      } else {
        for (i = offsets.length - 1; i >= 0; i--) {
          if (offsets[i] - inset < cur - 2) {
            target = offsets[i] - inset;
            break;
          }
        }
      }
      vp.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    }
    function updateArrows(panel) {
      var vp = viewportOf(panel);
      var prev = panel.querySelector('[data-np-tps-arrow="prev"]');
      var next = panel.querySelector('[data-np-tps-arrow="next"]');
      if (!vp || !prev || !next) return;
      var overflow = vp.scrollWidth - vp.clientWidth > 4;
      if (!overflow) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = false;
      next.hidden = false;
      prev.disabled = vp.scrollLeft <= 2;
      next.disabled = vp.scrollLeft >= vp.scrollWidth - vp.clientWidth - 2;
    }

    panels.forEach(function (panel) {
      var vp = viewportOf(panel);
      var prev = panel.querySelector('[data-np-tps-arrow="prev"]');
      var next = panel.querySelector('[data-np-tps-arrow="next"]');
      if (vp)
        vp.addEventListener(
          'scroll',
          function () {
            updateArrows(panel);
          },
          { passive: true }
        );
      if (prev)
        prev.addEventListener('click', function () {
          scrollByCard(vp, -1);
        });
      if (next)
        next.addEventListener('click', function () {
          scrollByCard(vp, 1);
        });

      if (vp) enableDragScroll(vp);
    });

    /* ── Mouse drag-to-scroll (free, no snap) ──────────────────────
       Touch + trackpad already scroll natively; this adds click-and-drag
       for mouse users on desktop. We use Pointer Events and only engage
       for the primary mouse button, leaving touch/pen to native scrolling
       (which is smoother and respects momentum). A small movement
       threshold distinguishes a drag from a click so the CTA still works. */
    function enableDragScroll(vp) {
      var down = false; // pointer is held
      var dragging = false; // movement passed the threshold
      var startX = 0;
      var startScroll = 0;
      var pointerId = null;
      var THRESHOLD = 4; // px before a press becomes a drag

      vp.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return; // mouse-only
        down = true;
        dragging = false;
        startX = e.clientX;
        startScroll = vp.scrollLeft;
        pointerId = e.pointerId;
      });

      vp.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!dragging) {
          if (Math.abs(dx) < THRESHOLD) return;
          dragging = true;
          vp.classList.add('is-dragging');
          try {
            vp.setPointerCapture(pointerId);
          } catch (err) {}
        }
        e.preventDefault();
        vp.scrollLeft = startScroll - dx;
      });

      function endDrag() {
        if (!down) return;
        down = false;
        if (dragging) {
          vp.classList.remove('is-dragging');
          try {
            vp.releasePointerCapture(pointerId);
          } catch (err) {}
          // Tell the root click handler to swallow the synthetic click the
          // browser fires after this drag, so a swipe never activates a
          // card/CTA. Cleared by that handler (or after a tick, in case the
          // browser fires no click at all — e.g. drag released off-target).
          dragJustEnded = true;
          setTimeout(function () {
            dragJustEnded = false;
          }, 50);
        }
        dragging = false;
        pointerId = null;
      }

      vp.addEventListener('pointerup', endDrag);
      vp.addEventListener('pointercancel', endDrag);
      vp.addEventListener('pointerleave', endDrag);
      // Prevent native image-drag ghosting while dragging the row.
      vp.addEventListener('dragstart', function (e) {
        if (dragging) e.preventDefault();
      });
    }

    window.addEventListener('resize', function () {
      var active = root.querySelector('.np-tps__panel:not([hidden])');
      if (active) updateArrows(active);
    });

    /* Initial arrow state for the first (visible) panel. */
    var first = root.querySelector('.np-tps__panel:not([hidden])');
    if (first) {
      // Images may still be loading; recheck after a tick + on full load.
      updateArrows(first);
      setTimeout(function () {
        updateArrows(first);
      }, 300);
      window.addEventListener('load', function () {
        updateArrows(first);
      });
    }

    /* Enable the hover-sweep animations only AFTER first paint, so the
       one-directional OUT keyframe never plays on initial load (no flash). */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('np-tps--ready');
      });
    });
  }

  function initAll() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-np-tps]'));
    roots.forEach(initRoot);
    // Bootstrap the shared quick-buy modal even on pages with NO slider (e.g. Shop
    // All), so window.NPTps.openModal exists for external callers. We run initRoot
    // against a detached placeholder: it wires the shared [data-np-tps-modal] element
    // and exposes the opener, while every slider-specific query (tabs/panels/arrows/
    // drag) simply finds nothing and no-ops. Guarded so it happens at most once.
    if (!roots.length && !window.__npTpsModalBootstrapped && document.querySelector('[data-np-tps-modal]')) {
      window.__npTpsModalBootstrapped = true;
      initRoot(document.createElement('div'));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init after Replo/theme re-renders inject new sliders.
  document.addEventListener('shopify:section:load', initAll);
})();
  